import { useTranslation } from '@conekta/cronos/i18n';
import { Icon } from '@conekta/frontino-ui/Icon';
import { IconType } from '@conekta/frontino-ui/Icon/Icon';
import { CheckoutFrameContext } from 'app/context';
import { parseAmountToCurrency } from 'app/util/currency';
import { calculateStartDate, calculateEndDate } from 'app/util/Subscriptiondates';
import { SubscriptionInterval } from 'common/interface';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale'; // Import English locale
import React, { useContext } from 'react';
import './styles.scss';

const DATE_FORMAT = 'dd MMMM yyyy';
const UNDEFINED_PLAN_COUNT = 9999999;
const T_ROOT = 'iFrame.checkout.subscription';

const SubscriptionDetail = () => {
  const { plan } = useContext(CheckoutFrameContext);
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? es : enUS;

  const intervalMap: Record<SubscriptionInterval, string> = {
    month: t(`${T_ROOT}.planDetails.interval.month`),
    week: t(`${T_ROOT}.planDetails.interval.week`),
    year: t(`${T_ROOT}.planDetails.interval.year`),
    minute: t(`${T_ROOT}.planDetails.interval.minute`),
    day: t(`${T_ROOT}.planDetails.interval.day`),
    half_month: t(`${T_ROOT}.planDetails.interval.half_month`),
  };

  if (!plan) {
    return (
      <div className='subscription-detail empty-state'>
        <div className='subscription-detail__header'>{t(`${T_ROOT}.emptyState.header`)}</div>
        <div className='subscription-detail__content'>
          <p>{t(`${T_ROOT}.emptyState.content`)}</p>
        </div>
      </div>
    );
  }

  const startDate = calculateStartDate(plan?.trialPeriodDays || 0);
  const endDate =
    plan?.expiryCount >= UNDEFINED_PLAN_COUNT
      ? null
      : calculateEndDate({
          startDate,
          interval: plan.interval,
          frequency: plan.frequency,
          expiryCount: plan.expiryCount,
        });

  return (
    <div className='subscription-detail'>
      <div className='subscription-detail__header'>{plan.name}</div>
      <div className='subscription-detail__content'>
        <div className='subscription-detail__price'>
          <span className='subscription-detail__price-value'>
            {parseAmountToCurrency('MXN', plan.amount)} {plan.currency}
          </span>
          {plan.trialPeriodDays > 0 && (
            <span className='subscription-detail__trial'>
              {plan.trialPeriodDays}{' '}
              {plan.trialPeriodDays === 1
                ? t(`${T_ROOT}.planDetails.trial.singular`)
                : t(`${T_ROOT}.planDetails.trial.plural`)}
            </span>
          )}
        </div>
        <p className='subscription-detail__period'>{intervalMap[plan.interval]}</p>
        <hr className='subscription-detail__divider' />
        <div className='subscription-detail__dates'>
          <div className='subscription-detail__dates-item'>
            <span className='subscription-detail__dates-label'>{t(`${T_ROOT}.planDetails.startDate`)}</span>
            <span className='subscription-detail__dates-value'>{format(startDate, DATE_FORMAT, { locale })}</span>
          </div>
          <div className='subscription-detail__dates-item'>
            {endDate ? (
              <>
                <span className='subscription-detail__dates-label'>{t(`${T_ROOT}.planDetails.endDate`)}</span>
                <span className='subscription-detail__dates-value'>{format(endDate, DATE_FORMAT, { locale })}</span>
              </>
            ) : (
              <span className='subscription-detail__dates-label'>{t(`${T_ROOT}.planDetails.noEndDate`)}</span>
            )}
          </div>
        </div>
        <hr className='subscription-detail__divider' />
        <div className='subscription-detail__info'>
          <div className='subscription-detail__info-item'>
            <Icon className='subscription-detail__icon' type={IconType.OUTLINED} size='small'>
              check_circle
            </Icon>
            {t(`${T_ROOT}.planDetails.paymentInfo.autoCharge`)}
          </div>
          <div className='subscription-detail__info-item'>
            <Icon className='subscription-detail__icon' type={IconType.OUTLINED} size='small'>
              check_circle
            </Icon>
            {t(`${T_ROOT}.planDetails.paymentInfo.cancelAnytime`)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetail;
